import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import './styles/App.css';
import { UserContext, WorkspaceContext, ActiveMenuIndexContext, MenuContext, OrgContext, ShowGettingStartedContext, ForecastContext } from './objects/Context';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import ErrorHandler from './components/ErrorHandler/ErrorHandler';

// App Components
import Banner from './components/Banner/Banner';
import Header from './components/Header/Header';
// Getting Started
import GettingStarted from './components/GettingStarted/GettingStarted';
import Popup from './components/Popup/Popup';
import WhatsNew, { WHATS_NEW_STORAGE_KEY } from './components/WhatsNew/WhatsNew';
//Settings
import Settings from './pages/Settings/Settings';
import Definitions from './pages/Definitions/Definitions';
import Variance from './pages/Reports/Variance/Variance';
import Overview from './pages/Reports/Overview/Overview';
import Transactions from './pages/Reports/Transactions/Transactions';
// App Main Components
import Menu from './components/Menu/Menu';
import Threads from './pages/Threads/Threads';
import Dashboards from './pages/Dashboards/Dashboards';
import DashboardManager from './pages/Dashboards/DashboardManager';
// import TileEditor from './pages/TileEditor/TileEditor';
import DataSources from './pages/DataSources/DataSources';
// import DataSourceDetails from './pages/DataSources/DataSourceDetails';
import AddDataSource from './pages/DataSources/AddDataSource/AddDataSource';
// import Forecasts from './pages/Forecasts/Forecasts';
import Reports from './pages/Reports/Reports';
import DataTeam from './pages/AIDataTeam/DataTeam';
import TaskManager from './pages/AIDataTeam/TaskManager';
import Options from './pages/Options/Options';
import ProfitAndLoss from './pages/Statements/ProfitAndLoss';
import BalanceSheet from './pages/Statements/BalanceSheet';

import LoadingScreen from './components/LoadingScreen/LoadingScreen';
import Authentication from './pages/Authentication/Authentication';
// import WorkspaceCreation from './pages/Authentication/WorkspaceCreation';
import OrgCreation from './pages/Authentication/OrgCreation';
import OrgSelection from './pages/Authentication/OrgSelection';
import OAuthConsent from './pages/Authentication/OAuthConsent';
import { captureOAuthFlowFromUrl, getStoredOAuthFlow } from './utils/oauthFlow';

// ─── MAINTENANCE MODE — flip to true to show the maintenance screen ───────────
const MAINTENANCE_MODE = false;
// ─────────────────────────────────────────────────────────────────────────────

// Tiny redirect for /auth that respects the MCP `oauth_flow` query param
// when the user is already signed in. Captures the flow id for the
// org/workspace setup chain and routes directly to the consent page. Also
// honors a flow id already in localStorage (captured at App root mount or
// during a prior page visit) so we still recover the consent destination
// even if the URL's query string was dropped by an intermediate redirect.
function AuthRedirect() {
  const [searchParams] = useSearchParams();
  const urlFlowId = searchParams.get('oauth_flow');
  if (urlFlowId) {
    captureOAuthFlowFromUrl(searchParams);
    return <Navigate to={`/oauth-consent?flow=${urlFlowId}`} replace />;
  }
  const storedFlowId = getStoredOAuthFlow();
  if (storedFlowId) {
    return <Navigate to={`/oauth-consent?flow=${storedFlowId}`} replace />;
  }
  return <Navigate to="/forecast" replace />;
}

// Unauthenticated users hitting /oauth-consent directly: capture the flow id
// (so it survives the login flow) and redirect to /auth.
function UnauthOAuthConsentRedirect() {
  const [searchParams] = useSearchParams();
  captureOAuthFlowFromUrl(searchParams);
  return <Navigate to="/auth" replace />;
}

// Utility function to detect mobile devices and small screens
const isMobileOrSmallScreen = () => {
  // Check for mobile devices
  const isMobile = /iPhone|iPad|iPod|Android|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  // Check for small screen width (less than 768px)
  const isSmallScreen = window.innerWidth < 768;
  
  return isMobile || isSmallScreen;
};

function App() {
  const { setActiveMenuIndex, setActiveSubMenuText } = useContext(ActiveMenuIndexContext);
  const { user, setUser } = useContext(UserContext);
  const { isMenuCondensed, setIsMenuCondensed } = useContext(MenuContext);
  const { workspace, setWorkspace } = useContext(WorkspaceContext);
  const { org, setOrg } = useContext(OrgContext);
  const { showGettingStarted, setShowGettingStarted } = useContext(ShowGettingStartedContext);
  const { 
    forecasts, 
    setForecasts, 
    currentForecast, 
    setCurrentForecast,
    compiledForecast,
    setCompiledForecast,
    components,
    setComponents,
    componentsLoading,
    setComponentsLoading,
    forecastReloadKey,
    activeScenario,
    setScenarios
  } = useContext(ForecastContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const whatsNewShownRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Capture an MCP `?oauth_flow=...` query param the moment the app sees it,
  // BEFORE any of our session / org / workspace fetching navigates somewhere
  // else and drops the query string. Persisted to localStorage so the rest of
  // the auth chain (login, signup, org-setup, workspace-create) can route the
  // user to /oauth-consent on completion via getPostAuthDestination().
  useEffect(() => {
    captureOAuthFlowFromUrl(searchParams);
  }, [searchParams]);

  useEffect(() => {
    setActiveSubMenuText(null);
    if (location.pathname === '/forecast/transactions') {
      setActiveMenuIndex(1);
    } else if (location.pathname.startsWith('/forecast')) {
      setActiveMenuIndex(0);
    } else if (location.pathname.startsWith('/reports')) {
      setActiveMenuIndex(1);
    } else if (location.pathname.startsWith('/options')) {
      setActiveMenuIndex(2);
    } else if (location.pathname.startsWith('/statements/profit-and-loss')) {
      setActiveMenuIndex(3);
    } else if (location.pathname.startsWith('/statements/balance-sheet')) {
      setActiveMenuIndex(4);
    } else if (location.pathname.startsWith('/data-sources')) {
      setActiveMenuIndex(5);
    } else if (location.pathname.startsWith('/settings')) {
      setActiveMenuIndex(6);
    } else {
      setActiveMenuIndex(null);
      setActiveSubMenuText(null);
    }
  }, [location.pathname, setActiveMenuIndex]);

  useEffect(() => {
    const refreshSession = async () => {
      try {
        // Attempt to refresh the token
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/auth/refresh-session`, { withCredentials: true });
  
        if (response.data.success) {
          console.log('Token refreshed successfully.');
          if (!mounted) {
            setMounted(true);
            const email = response.data.email;
            fetchUserData(email);
          }
        } else {
          console.log('Token refresh failed.');
          setUser(null);
          setWorkspace(null);
          setLoading(false);
        }
      } catch (error) {
        console.log('No active session found or refresh token expired.');
        setUser(null);
        setWorkspace(null);
        setLoading(false);
      }
    };

    // Initial token refresh
    refreshSession();

    // Set up interval to refresh token every 55 minutes
    const intervalId = setInterval(refreshSession, 55 * 60 * 1000);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []);
  

  useEffect(() => {
    if (user?.id) {
      if (org?.id) {
        fetchWorkspace();
      } else {
        fetchOrg();
      }
    }
  }, [user, org]);

  useEffect(() => {
    if (workspace?.id && user?.id) {
      fetchForecasts(workspace.id);
    }
  }, [workspace?.id, user?.id]);

  // Show the "What's new this week" popup once per release per browser. Bump
  // WHATS_NEW_VERSION in WhatsNew.jsx to re-show after dismissal. Skip on
  // auth/oauth-consent routes and on small screens so the consent flow /
  // mobile experience aren't clobbered. The ref guards against re-triggering
  // on internal navigation within the same session.
  useEffect(() => {
    if (whatsNewShownRef.current) return;
    if (!org?.id || !workspace?.id) return;
    if (location.pathname === '/auth' || location.pathname === '/oauth-consent') return;
    if (isMobileOrSmallScreen()) return;
    try {
      if (localStorage.getItem(WHATS_NEW_STORAGE_KEY)) return;
    } catch (e) {
      return;
    }
    whatsNewShownRef.current = true;
    setShowWhatsNew(true);
  }, [org?.id, workspace?.id, location.pathname]);

  const handleCloseWhatsNew = () => {
    setShowWhatsNew(false);
    try {
      localStorage.setItem(WHATS_NEW_STORAGE_KEY, '1');
    } catch (e) { /* localStorage unavailable; popup just won't persist dismissal */ }
  };

  // Load compiled forecast when currentForecast or activeScenario changes
  useEffect(() => {
    if (!currentForecast?.id || !user?.id || !workspace?.id) {
      setCompiledForecast(null);
      setComponents(null);
      return;
    }

    const loadCompiledForecast = async () => {
      try {
        setComponentsLoading(true);
        const label = activeScenario || currentForecast.scenarioCase;
        console.log('Loading compiled forecast:', currentForecast.id, label);

        const { transformCashJsonToFrontend } = await import('./components/Forecast/utils/jsonTransformer');
        
        const url = activeScenario
          ? `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/scenarios/${currentForecast.id}/compile/${encodeURIComponent(activeScenario)}`
          : `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/compile/${currentForecast.id}/${user.id}/${workspace.id}`;

        const response = await axios.get(url, { withCredentials: true });
        
        if (response.data?.success && response.data?.cashJson) {
          const cashJsonData = response.data.cashJson;
          const { forecast: transformedForecast, components: transformedComponents } = transformCashJsonToFrontend(cashJsonData, {
            calculateFromFormulas: false
          });
          
          const fullForecast = {
            ...currentForecast,
            ...transformedForecast,
            status: currentForecast.status || transformedForecast.status,
            build_steps: currentForecast.build_steps || transformedForecast.build_steps
          };
          
          setCompiledForecast(fullForecast);
          setComponents(transformedComponents || []);
          console.log('Compiled forecast loaded successfully', activeScenario ? `(scenario: ${activeScenario})` : '(base)');
        } else {
          console.warn('No cash_json data returned for forecast:', currentForecast.id);
          setCompiledForecast(null);
          setComponents([]);
        }
      } catch (error) {
        console.error('Error loading compiled forecast:', error);
        setCompiledForecast(null);
        setComponents([]);
      } finally {
        setComponentsLoading(false);
      }
    };

    loadCompiledForecast();
  }, [currentForecast?.id, user?.id, workspace?.id, forecastReloadKey, activeScenario]);

  // Fetch overlay scenarios whenever the base forecast changes
  useEffect(() => {
    if (!currentForecast?.id) {
      setScenarios([]);
      return;
    }
    const fetchScenarios = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/scenarios/${currentForecast.id}/list`,
          { withCredentials: true }
        );
        if (response.data?.success) {
          setScenarios(response.data.scenarios || []);
        }
      } catch (err) {
        console.error('Error fetching scenarios:', err);
      }
    };
    fetchScenarios();
  }, [currentForecast?.id, forecastReloadKey]);

  const fetchWorkspace = async () => {
    try {
        const workspaceResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/get-workspace/${user.id}/${user?.activeWorkspaceId || 'null'}/${org.id}`, { withCredentials: true });
        console.log("Successfully retrieved workspace.");
        const workspaceData = workspaceResponse.data.workspace;
        setWorkspace(workspaceData);
    } catch (err) {
        console.log('No workspace found for user. Alerting user.');
        setWorkspace(null);
        setError("There was an error loading your workspace. Please try again or contact support.");
    } finally {
      setLoading(false);
    }
  }

  const fetchForecasts = async (workspaceId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/forecasts/list/${workspaceId}/${user.id}`, { withCredentials: true });
      console.log("Successfully retrieved forecasts.");
      const forecastsData = response.data || [];
      setForecasts(forecastsData);

      console.log("Forecasts:", forecastsData);
      
      // Set current forecast to "Base" (assume it exists)
      const baseForecast = forecastsData.find(f => 
        f.scenarioCase && f.scenarioCase.toLowerCase() === 'base'
      );
      
      if (baseForecast) {
        setCurrentForecast(baseForecast);
        console.log(`Base forecast loaded: ${baseForecast.id}`);
      }
    } catch (err) {
      console.error('Failed to fetch forecasts:', err);
      setForecasts([]);
      setCurrentForecast(null);
    }
  }

  const fetchOrg = async () => {
    setLoading(true);
    const orgIds = user.orgIds || [];

    if (!user.orgId) {
      if (orgIds.length === 0) {
        console.log('No org found for user. Routing to org setup.');
        navigate('/org-setup');
        setOrg(null);
        setWorkspace(null);
        setLoading(false);
        return;
      }

      // If the user has multiple orgs, or any pending invites to other orgs,
      // route to the selection screen so they can switch or accept an invite.
      let hasPendingInvites = false;
      if (user.email) {
        try {
          const invitesResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/get-org-invites/${user.email}`, { withCredentials: true });
          hasPendingInvites = (invitesResponse.data?.invites?.length || 0) > 0;
        } catch (err) {
          hasPendingInvites = false;
        }
      }

      if (orgIds.length > 1 || hasPendingInvites) {
        console.log('Routing to org selection (multiple orgs or pending invites).');
        navigate('/select-org');
        setOrg(null);
        setWorkspace(null);
        setLoading(false);
        return;
      }
      // orgIds.length === 1 and no invites: auto-select the only org
    }

    const activeOrgId = user.orgId || orgIds[0];
    try {
        const orgResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/orgs/get-org/${activeOrgId}`, { withCredentials: true });
        console.log("Successfully retrieved org.");
        setOrg(orgResponse.data.org);
    } catch (err) {
        console.log('No org found for user. Routing to org setup.');
        setOrg(null);
        setWorkspace(null);
        setLoading(false);
        navigate('/org-setup');
    }
  }

  const fetchUserData = async (userEmail) => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/get-user-by-email/${userEmail}`, { withCredentials: true });
      console.log('Successfully retrieved user.');
      const user = response.data[0];
      setUser(user);
      // Check if user has gettingStarted flag and set the context accordingly
      if (user.gettingStarted) {
        setShowGettingStarted(true);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setUser(null);
      setWorkspace(null);
      setOrg(null);
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading Workspace..." />;
  }
  if (error) {
    return <ErrorHandler errorMessage={error} />;
  }
  
  // Standalone routes (no app shell) that need to work across all auth states:
  // /oauth-consent and /auth (when already signed in). React-router matches
  // these first, falling through to the normal app routing otherwise.
  const isOnAuthRoute = location.pathname === '/auth';
  const isOnOAuthConsent = location.pathname === '/oauth-consent';

  return (
    <div className="app-wrapper">
      {user?.id ? (
        isOnAuthRoute ? (
          <AuthRedirect />
        ) : isOnOAuthConsent ? (
          // Render consent only once org+workspace are both loaded. While they
          // load, show a spinner — do NOT redirect to org-setup based on a
          // null org alone, since fetchOrg may not have run yet (race with
          // the post-login navigate). Only kick out to org-setup when the
          // user genuinely has no org membership.
          (org?.id && workspace?.id) ? (
            <OAuthConsent />
          ) : (user?.orgId || (user?.orgIds?.length > 0)) ? (
            <LoadingScreen message="Loading Workspace..." />
          ) : (
            <Navigate to="/org-setup" replace />
          )
        ) : (org?.id && workspace?.id) ? (
          <>
            <Banner />
            <Header />
            <main className="product-main">
                <>
                  <Menu />
                  {/* {showGettingStarted && !isMobileOrSmallScreen() && <Popup content={<GettingStarted />} onClose={() => {setShowGettingStarted(false)}}/>} */}
                  {showWhatsNew && (
                    <Popup
                      content={<WhatsNew onClose={handleCloseWhatsNew} />}
                      onClose={handleCloseWhatsNew}
                    />
                  )}
                  <Routes>
                    <Route path="/forecast" element={<Threads />} />
                    <Route path="/reports" element={<Reports />} />
                    <Route path="/reports/overview" element={<Overview />} />
                    <Route path="/reports/variance" element={<Variance />} />
                    <Route path="/reports/transactions" element={<Transactions />} />
                    <Route path="/forecast/transactions" element={<Transactions />} />
                    <Route path="/statements/profit-and-loss" element={<ProfitAndLoss />} />
                    <Route path="/statements/balance-sheet" element={<BalanceSheet />} />
                    <Route path="/options" element={<Options />} />
                    <Route path="/definitions" element={<Definitions />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/data-sources" element={<DataSources />} />
                    <Route path="*" element={<Navigate to="/forecast" />} />
                  </Routes>
                </>
            </main>
          </>
        ) : org?.id ? (
          <LoadingScreen message="Loading Workspace..." />
        ) : (
          <Routes>
            <Route path="/org-setup" element={<OrgCreation />} />
            <Route path="/select-org" element={<OrgSelection />} />
            <Route path="*" element={<Navigate to={user?.orgIds?.length > 1 ? "/select-org" : "/org-setup"} />} />
          </Routes>
        )
      ) : (
        <Routes>
          <Route path="/auth" element={<Authentication />} />
          <Route path="/oauth-consent" element={<UnauthOAuthConsentRedirect />} />
          <Route path="*" element={<Navigate to="/auth" />} />
        </Routes>
      )}
    </div>
  );
}

function MaintenanceScreen() {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100dvh',
      width: '100vw',
      background: '#f5f8ff',
      fontFamily: 'var(--font-regular, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif)',
      textAlign: 'center',
      padding: '0 24px',
      boxSizing: 'border-box',
    }}>
      <div style={{
        background: '#fff',
        border: '1px solid #e3ecfd',
        borderRadius: '18px',
        padding: '52px 56px',
        maxWidth: '480px',
        width: '100%',
        boxShadow: '0 4px 24px rgba(72, 143, 243, 0.07)',
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '14px',
          background: 'linear-gradient(135deg, #e8f1fe 0%, #d0e4fc 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#488FF3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" />
            <path d="M2 12l10 5 10-5" />
          </svg>
        </div>
        <h1 style={{
          margin: '0 0 10px',
          fontSize: '22px',
          fontWeight: '650',
          letterSpacing: '-0.3px',
          color: '#1a2540',
        }}>
          Vairo update currently in progress
        </h1>
        <p style={{
          margin: '28px 0 28px',
          fontSize: '14.5px',
          lineHeight: '1.75',
          color: '#7a8baa',
        }}>
          We're rolling out improvements right now! Please check back shortly.
        </p>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '7px',
          background: '#f0f6ff',
          border: '1px solid #d0e4fc',
          borderRadius: '999px',
          padding: '7px 16px',
          fontSize: '13px',
          color: '#488FF3',
          fontWeight: '500',
        }}>
          <span style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: '#488FF3',
            display: 'inline-block',
            animation: 'mPulse 1.8s ease-in-out infinite',
          }} />
          Maintenance in progress
        </div>
      </div>
      <style>{`
        @keyframes mPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(0.8); }
        }
      `}</style>
    </div>
  );
}

export default function AppWrapper() {
  if (MAINTENANCE_MODE) {
    return <MaintenanceScreen />;
  }
  return (
    <Router>
      <App />
    </Router>
  );
}
